import React, { useState } from 'react';
import { Typography, Modal, Checkbox, Spin, Pagination, Table, Select, Tag, Alert } from 'antd';
import { getAccounts } from '~/api/accounts';
import { Account } from '~/types';
import { useTranslation } from 'react-i18next';
import AccountFilters, { AccountFiltersType } from '~/pages/accounts/components/Filters';
import layout from '~/hooks/layout';
import { usePageQuery } from '~/hooks/usePageQuery';
import onError from '~/errorsHandler';
import { useQuery } from 'react-query';
import { MinusSquareOutlined } from '@ant-design/icons';
import theme from '~/theme';
import { CSSProperties } from 'styled-components';
import { Form } from '~/components';

interface AccountModalSelectorProps {
  selectorForm: any;
  selectMode: 'multiple' | 'single';
  name: string;
  label?: React.ReactNode;
  placeholder?: React.ReactNode;
  beforeOkCheck?: (selected: Account[]) => boolean;
  style?: CSSProperties;
  required?: boolean;
  onChange?: () => void;
  warning?: React.ReactNode;
}

type Filters = AccountFiltersType;

function cleanFilters(filters: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );
}

const AccountModalSelector: React.FC<AccountModalSelectorProps> = ({
  selectorForm,
  name,
  selectMode,
  label,
  placeholder,
  beforeOkCheck,
  style,
  required,
  onChange,
  warning,
}) => {
  const [t] = useTranslation('terminals');
  const [form] = Form.useForm();
  const initialSelected = selectorForm?.getFieldValue(name) ?? [];

  const isMobile = layout.useIsMobile();
  const [openAccountsModal, setOpenAccountsModal] = useState(false);
  const [selected, setSelected] = useState<Account[]>([]);
  const [filters, setFilters] = useState<Filters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: selectedDataInit, isLoading: isLoadingSelectedData } = useQuery(
    ['accountsByIds', ...initialSelected],
    () => getAccounts({ ids: initialSelected }),
    {
      enabled: initialSelected && initialSelected.length > 0,
      onSuccess: (data) => {
        initialSelected && initialSelected.length > 0 && setSelected(data?.data);
      },
    }
  );

  const cleanedFilters = cleanFilters({ ...filters, page: currentPage, perPage: pageSize });

  const queryKey = ['accounts', cleanedFilters];

  const {
    data: accountsData,
    total,
    isLoading: accountsLoading,
  } = usePageQuery<Account[]>(queryKey, () => getAccounts(cleanedFilters), {
    enabled: true,
  });
  const handleFilterChange = (newFilters: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleAccountSelect = (accountId: string) => {
    const account = accountsData?.find((acc) => acc.id === accountId);
    if (!account) return;
    setSelected((prev) => {
      if (selectMode === 'single') {
        return prev.some((acc) => acc.id === accountId) ? [] : [account];
      }
      return prev.some((acc) => acc.id === accountId)
        ? prev.filter((acc) => acc.id !== accountId)
        : [...prev, account];
    });
  };

  const handleAccountsModalOk = () => {
    if (beforeOkCheck && !beforeOkCheck(selected)) {
      const newError = new Error(t('accountsCurrencyError'));
      onError(newError);
      return;
    }
    if (selectMode === 'single') {
      selectorForm.setFieldsValue({ [name]: selected[0]?.id });
      onChange && onChange();
    } else {
      selectorForm.setFieldsValue({ [name]: selected.map((acc) => acc.id) });
    }
    setFilters({});
    form.resetFields();
    setCurrentPage(1);
    setOpenAccountsModal(false);
  };

  const columns = [
    {
      title: (
        <MinusSquareOutlined
          style={{
            cursor: 'pointer',
            color: selected?.length ? theme.primaryColor : theme.disabledColor,
          }}
          onClick={() => {
            setSelected([]);
          }}
          title={t('deselectAll')}
        />
      ),
      dataIndex: 'id',
      key: 'checkbox',
      render: (_: any, record: Account) => (
        <Checkbox
          checked={selected.some((acc) => acc.id === record.id)}
          onChange={() => handleAccountSelect(record.id)}
        />
      ),
      width: 48,
    },
    {
      title: t('account'),
      dataIndex: 'number',
      key: 'number',
      render: (_: any, record: Account) => record?.number,
    },
    {
      title: t('alias'),
      dataIndex: 'alias',
      key: 'alias',
      render: (_: any, record: Account) => (
        <Typography.Text
          ellipsis={{ tooltip: record?.alias }}
          style={{ maxWidth: 250, display: 'inline-block', verticalAlign: 'middle' }}
        >
          {record?.alias}
        </Typography.Text>
      ),
    },
    {
      title: t('currency'),
      dataIndex: 'currency',
      key: 'currency',
      render: (_: any, record: Account) => record?.currency,
    },
  ];

  const onDialogCancel = () => {
    setOpenAccountsModal(false);
    selectorForm.setFieldsValue({
      [name]: initialSelected,
    });
    onChange && onChange();
    setSelected(selectedDataInit?.data || []);
    setFilters({});
    form.resetFields();
    setCurrentPage(1);
  };
  return (
    <>
      <Form.Item
        name={name}
        label={label}
        rules={required ? [{ required: true }] : undefined}
        help={required && null}
      >
        <Select
          mode={selectMode === 'multiple' ? 'multiple' : undefined}
          open={false}
          value={
            selectMode === 'single' ? selected[0]?.id || undefined : selected.map((acc) => acc.id)
          }
          onDropdownVisibleChange={(visible) => visible && setOpenAccountsModal(true)}
          tagRender={
            selectMode === 'multiple'
              ? ({ value }) => {
                  const acc = (selected || [])?.find((a) => a.id === value);
                  if (!acc) {
                    return <Tag style={{ display: 'none' }} />;
                  }
                  return (
                    <Tag
                      key={value}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                      }}
                      style={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                        verticalAlign: 'middle',
                      }}
                      title={acc ? acc?.alias || acc?.number : value}
                    >
                      {acc ? acc?.number : value}
                    </Tag>
                  );
                }
              : undefined
          }
          optionLabelProp={selectMode === 'single' ? 'children' : undefined}
          placeholder={placeholder}
          style={{ width: '100%', ...style }}
          loading={accountsLoading || isLoadingSelectedData}
          allowClear={!required}
          onClear={() => {
            setSelected([]);
            selectorForm.setFieldsValue({ [name]: selectMode === 'single' ? undefined : [] });
            onChange && onChange();
          }}
        >
          {selectMode === 'single' && selected.length > 0 && (
            <Select.Option value={selected[0]?.id} key={selected[0]?.id}>
              {selected[0]?.number || selected[0]?.id}
            </Select.Option>
          )}
        </Select>
      </Form.Item>
      <Modal
        title={selectMode === 'multiple' ? t('selectAccounts') : t('selectAccount')}
        open={openAccountsModal}
        onOk={handleAccountsModalOk}
        okButtonProps={required ? { disabled: selected.length === 0 } : {}}
        onCancel={onDialogCancel}
        width={isMobile ? '100vw' : 800}
        style={isMobile ? { top: 0, padding: 0 } : undefined}
      >
        {warning && (
          <Alert type="warning" message={warning} showIcon style={{ marginBottom: 16 }} />
        )}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <AccountFilters onChange={handleFilterChange} form={form} />
        </div>
        {accountsLoading ? (
          <Spin />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={accountsData}
            pagination={false}
            size="small"
            style={isMobile ? { overflowX: 'auto' } : {}}
          />
        )}
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={total}
          onChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
          style={{ marginTop: 16, textAlign: 'center' }}
        />
      </Modal>
    </>
  );
};

export default AccountModalSelector;
